import React, {useEffect, useRef} from 'react';

import * as ViewHooks from '../state/views/hooks';
import type * as PanelViewTypes from '../state/views/panel/types';
import type * as PanelBankSectionConfigTypes from '../state/views/panelBankSectionConfig/types';
import {getPanelSpec} from '../util/panelTypes';
import {PanelContextProvider} from './Panel';
import PanelEditor, {
  type PanelEditorProps,
} from './Panel/PanelEditor/PanelEditor';
import {SharedPanelStateContextProvider} from './Panel/SharedPanelStateContext';
import type {OrganizedSettings} from './PanelBank/types';
import {InstrumentedLoader as Loader} from './utility/InstrumentedLoader';

type AddVisNewPanelEditorProps = Omit<
  PanelEditorProps,
  'panelSpec' | 'panelRef' | 'onOK'
> & {
  panel: PanelViewTypes.Panel;
  /**
   * Optional because the target section may not exist yet
   * when adding a panel at the global/workspace level
   */
  panelBankSectionConfigRef?: PanelBankSectionConfigTypes.Ref;
  noEdit?: boolean;
  onOK(panel: PanelViewTypes.Ref): void;
  inheritedSettings?: OrganizedSettings;
};

const EMPTY_CONFIG = {};
export const AddVisNewPanelEditor = (props: AddVisNewPanelEditorProps) => {
  const panelRef = ViewHooks.useNewObjectRef('panel', props.panel);

  // If this is a panel that doesnt have an edit mode, then
  // immediately call onOK when our callback is ready.
  //
  // onOK is a one-time side effect (it adds the panel/section and then unmounts
  // this editor via setNewPanel(undefined)). It must fire exactly once. Under
  // React 18 concurrent rendering the addSection/addPanel dispatch can force a
  // synchronous re-render of this component (react-redux v8 useSyncExternalStore
  // tear-prevention) before the unmount commits, so without a guard this effect
  // would re-run and call onOK a second time — creating a duplicate panel/section.
  const hasFiredOnOK = useRef(false);
  useEffect(() => {
    if (hasFiredOnOK.current) {
      return;
    }
    const panelSpec = getPanelSpec(props.panel.viewType);
    if (
      panelRef.ready &&
      (panelSpec.noEditMode ||
        props.noEdit ||
        (panelSpec.openSettingsDrawerOnCreate &&
          panelSpec.settingsDrawerContent))
    ) {
      hasFiredOnOK.current = true;
      props.onOK(panelRef.ref);
    }
  });

  if (!panelRef.ready) {
    return <Loader name="add-vis-new-panel-editor-loader" />;
  }

  const spec = getPanelSpec(props.panel.viewType);

  return (
    <SharedPanelStateContextProvider config={EMPTY_CONFIG}>
      <PanelContextProvider
        inheritedSettings={props.inheritedSettings}
        panelRef={panelRef.ref}
        panelBankSectionConfigRef={props.panelBankSectionConfigRef}>
        <PanelEditor
          {...props}
          panelSpec={spec}
          panelRef={panelRef.ref}
          newPanel
          onOK={() => props.onOK(panelRef.ref)}
        />
      </PanelContextProvider>
    </SharedPanelStateContextProvider>
  );
};
